import {
  FileText,
  ShieldCheck,
  UserCheck,
  Info,
  Handshake,
  XCircle,
  Users,
  Flag,
  Lock,
  Server,
  Layers,
  Copyright,
  AlertTriangle,
  Scale,
  RefreshCw,
  Gavel,
  Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '@/components/SEO';

const Section = ({ icon: Icon, title, children, iconClass = 'text-blue-600' }) => (
  <section>
    <h2 className="text-2xl font-bold mb-4 flex items-center space-x-3">
      {Icon && <Icon className={`w-6 h-6 ${iconClass}`} />}
      <span>{title}</span>
    </h2>
    <div className="space-y-3 leading-relaxed">{children}</div>
  </section>
);

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-cyan-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <SEO
        title="Terms of Service — Toonah"
        description="Terms of service for using Toonah's anonymous one-on-one video chat platform."
        path="/terms"
      />
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-card">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/')}
            data-testid="terms-back-home"
            className="text-2xl font-black"
          >
            ← Toonah
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20" data-testid="terms-page">
        <div className="glass-card p-8 md:p-12 rounded-3xl">
          <div className="flex items-center space-x-4 mb-3">
            <FileText className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-black">Terms of Service</h1>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-10">
            Last updated: August 2026
          </p>

          <div className="space-y-10 text-slate-700 dark:text-slate-300">
            <Section icon={ShieldCheck} title="1. Acceptance of Terms">
              <p>
                By accessing or using Toonah, you agree to these Terms of Service and our{' '}
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
                >
                  Privacy Policy
                </button>
                . If you do not agree to these Terms, do not use Toonah.
              </p>
            </Section>

            <Section icon={UserCheck} title="2. Eligibility — 18+ Only">
              <p>
                Toonah is intended only for users who are 18 years of age or older. By using
                Toonah, you confirm that you are at least 18 years old and legally permitted
                to use the service in your jurisdiction. Do not use Toonah if you are under 18.
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Toonah relies on your confirmation of eligibility and does not perform
                identity or age verification.
              </p>
            </Section>

            <Section icon={Info} title="3. What Toonah Is">
              <p>
                Toonah is a platform for spontaneous, one-on-one conversations between people
                who may not know each other. Users are matched with another available person
                and can communicate using live audio and video.
              </p>
              <p>
                No registration or user profile is required. Users control when they reveal
                themselves and may leave or move to another conversation at any time.
              </p>
            </Section>

            <Section icon={Handshake} title="4. Respectful Behavior" iconClass="text-green-600">
              <p>
                Treat other users with respect. Toonah is intended for genuine conversations
                between people. You must not use Toonah to harm, threaten, exploit, deceive,
                intimidate, or deliberately make other users unsafe.
              </p>
            </Section>

            <Section icon={XCircle} title="5. Prohibited Conduct" iconClass="text-red-600">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
                <p className="font-semibold text-red-900 dark:text-red-200 mb-3">
                  The following is prohibited:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 text-red-800 dark:text-red-300">
                  <li>Harassment, bullying, threats, stalking, or intimidation.</li>
                  <li>Nudity, sexually explicit conduct, or sexual activity.</li>
                  <li>Any sexual exploitation or sexual content involving minors.</li>
                  <li>Hate speech or discriminatory abuse.</li>
                  <li>Sharing another person's private or identifying information without permission.</li>
                  <li>Fraud, scams, phishing, spam, or unsolicited commercial promotion.</li>
                  <li>Impersonating another person or misrepresenting your identity for fraudulent or harmful purposes.</li>
                  <li>Illegal activity or encouraging illegal activity.</li>
                  <li>Attempting to distribute malware, malicious links, or interfere with the service.</li>
                  <li>Attempting to bypass bans, security controls, or technical restrictions.</li>
                  <li>Using automated systems, bots, scraping tools, or similar mechanisms to abuse the service.</li>
                  <li>
                    Recording, capturing, publishing, distributing, or commercially exploiting
                    another user's private conversation, audio, video, or image without their
                    consent, except where permitted by applicable law.
                  </li>
                </ul>
              </div>
            </Section>

            <Section icon={Users} title="6. Your Safety and Interactions With Others">
              <p>
                Toonah connects you with people you may not know. You are responsible for your
                interactions with other users.
              </p>
              <p>
                Do not share passwords, OTPs, banking or financial information, your exact
                address, or other sensitive information with strangers.
              </p>
              <p>
                Toonah does not guarantee the identity, conduct, intentions, or truthfulness
                of another user. If a conversation makes you uncomfortable, you can leave the
                conversation and use the Report function where appropriate.
              </p>
            </Section>

            <Section icon={Flag} title="7. Reporting and Enforcement">
              <p>
                Users can report inappropriate behavior through Toonah's reporting system.
                Reports may be reviewed by administrators and appropriate action may be taken.
              </p>
              <p>
                Violations of these Terms may result in temporary or permanent restriction
                from using Toonah. Repeated or serious violations may result in stronger
                restrictions.
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Toonah does not guarantee that every violation will be detected, prevented,
                or acted upon immediately.
              </p>
            </Section>

            <Section icon={Lock} title="8. Privacy and Live Conversations">
              <p>
                Privacy practices are described in Toonah's{' '}
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
                >
                  Privacy Policy
                </button>
                . No registration is required. Temporary anonymous session identifiers may be
                used to operate the service. Live audio and video are used for conversations,
                and Toonah does not claim to permanently store live audio or video streams.
              </p>
            </Section>

            <Section icon={Server} title="9. Service Availability">
              <p>
                Toonah may occasionally be unavailable, interrupted, changed, maintained, or
                discontinued. We do not guarantee uninterrupted or error-free availability.
              </p>
              <p>
                We may modify, suspend, restrict, or discontinue all or part of the service
                when reasonably necessary, including for security, maintenance, legal, or
                operational reasons.
              </p>
            </Section>

            <Section icon={Layers} title="10. Access Restrictions">
              <p>
                We may restrict access to Toonah when we reasonably believe a user has:
              </p>
              <ul className="list-disc list-outside ml-5 space-y-1">
                <li>violated these Terms,</li>
                <li>created a safety or security risk,</li>
                <li>abused the service,</li>
                <li>attempted to circumvent restrictions, or</li>
                <li>engaged in unlawful activity.</li>
              </ul>
              <p>
                Restrictions may be temporary or permanent depending on the nature and
                severity of the conduct.
              </p>
            </Section>

            <Section icon={Copyright} title="11. Intellectual Property">
              <p>
                The Toonah name, branding, website design, software, graphics, and other
                materials provided by Toonah are protected by applicable intellectual-property
                laws. These Terms do not grant users ownership of Toonah's software, branding,
                or other proprietary materials.
              </p>
            </Section>

            <Section icon={Server} title="12. Third-Party Services">
              <p>
                Toonah may rely on third-party infrastructure and technology providers to
                operate portions of the service. Their services may be subject to their own
                terms, policies, availability, and technical limitations.
              </p>
            </Section>

            <Section icon={AlertTriangle} title="13. Disclaimer" iconClass="text-yellow-600">
              <p>
                Toonah enables conversations between people who may not know each other.
                While we provide privacy controls, reporting tools, and administrative safety
                measures, we cannot guarantee the identity, conduct, intentions, or behavior
                of other users.
              </p>
              <p>
                Use Toonah at your own discretion and discontinue any conversation that makes
                you uncomfortable or unsafe.
              </p>
              <p>
                To the extent permitted by applicable law, the service is provided without
                guarantees that it will always be uninterrupted, error-free, or suitable for
                every purpose.
              </p>
              <p>
                Nothing in these Terms excludes any rights or protections that cannot lawfully
                be excluded.
              </p>
            </Section>

            <Section icon={Scale} title="14. Limitation of Liability">
              <p>
                To the maximum extent permitted by applicable law, Toonah and its operators
                will not be liable for indirect, incidental, special, consequential, or
                similar losses arising from use of or inability to use the service.
              </p>
              <p>
                Nothing in these Terms limits liability where such limitation is prohibited
                by applicable law.
              </p>
            </Section>

            <Section icon={RefreshCw} title="15. Changes to These Terms">
              <p>
                We may update these Terms as Toonah evolves or as legal, safety, or
                operational requirements change. When we make material changes, we will
                update the &ldquo;Last updated&rdquo; date on this page.
              </p>
              <p>
                Your continued use of Toonah after updated Terms become effective means you
                agree to the revised Terms.
              </p>
            </Section>

            <Section icon={Gavel} title="16. Governing Law">
              <p>
                These Terms are governed by the laws of India. Subject to applicable law,
                disputes relating to Toonah or these Terms shall be subject to the
                jurisdiction of the courts in Hyderabad, Telangana, India.
              </p>
            </Section>

            <Section icon={Mail} title="17. Contact">
              <p>
                For questions about these Terms or Toonah, contact:{' '}
                <a
                  href="mailto:nirmalrajupdates@gmail.com"
                  data-testid="terms-contact-email"
                  className="text-blue-600 hover:text-blue-700 underline underline-offset-4"
                >
                  nirmalrajupdates@gmail.com
                </a>
              </p>
            </Section>

            <section className="pt-6 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500">Last updated: August 2026</p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
